/**
 * The Dynamix chunk container — the universal framing under every
 * structured Red Baron asset (screens, sprites, palettes, fonts, animation
 * scripts, music banks, sound-driver overlays).
 *
 * See docs/formats/chunk-container.md (VERIFIED). Layout, 8-byte header:
 *
 * ```
 * offset 0x00  char[3] tag        3-character ASCII identifier
 * offset 0x03  char    ':'        literal colon (0x3A)
 * offset 0x04  uint32le rawSize   bit 31 = container flag, bits 0-30 = payload size
 * offset 0x08  byte[size] payload container children, or leaf bytes
 * ```
 *
 * Container chunks are never themselves compressed; only leaf payloads can
 * carry a compression header (see compression.ts) — and only some of
 * those, per a per-format table this module does not know about. This
 * module only walks the tag/size tree; the (extension, tag) dispatch that
 * decides whether a given leaf is compressed belongs to the format-specific
 * decoders (scr.ts, bmp.ts, pal.ts, ...), by explicit design — see the
 * "do not sniff" trap documented in chunk-container.md.
 */

import { BinaryReader } from "./binary-reader.ts";

/** A container chunk: its payload is itself a sequence of further chunks. */
export interface ChunkContainer {
  readonly kind: "container";
  readonly tag: string;
  /** Byte offset of this chunk's 8-byte header within the buffer it was parsed from. */
  readonly headerOffset: number;
  /** Byte offset of this chunk's payload (headerOffset + 8). */
  readonly payloadOffset: number;
  /** Payload length in bytes (the tree beneath this node spans exactly this many bytes). */
  readonly byteLength: number;
  readonly children: readonly ChunkNode[];
}

/** A leaf chunk: its payload is raw bytes, not further chunks. */
export interface ChunkLeaf {
  readonly kind: "leaf";
  readonly tag: string;
  readonly headerOffset: number;
  readonly payloadOffset: number;
  readonly byteLength: number;
  /** Raw, as-stored payload bytes — still compressed if this leaf carries a compression header. */
  readonly data: Uint8Array;
}

export type ChunkNode = ChunkContainer | ChunkLeaf;

const CONTAINER_FLAG = 0x80000000;
const SIZE_MASK = 0x7fffffff;

/**
 * Parses one chunk header at the reader's current position and returns the
 * resulting node (recursing into children for a container), leaving the
 * cursor positioned just past this chunk's payload.
 */
function parseOne(reader: BinaryReader): ChunkNode {
  const headerOffset = reader.tell();
  const tagBytes = reader.bytes(3);
  const colon = reader.u8();
  if (colon !== 0x3a) {
    throw new Error(
      `chunk: expected ':' (0x3A) after tag at offset ${headerOffset}, got 0x${colon.toString(16).padStart(2, "0")}`,
    );
  }
  const tag = String.fromCharCode(tagBytes[0]!, tagBytes[1]!, tagBytes[2]!) + ":";
  const rawSize = reader.u32();
  const isContainer = (rawSize & CONTAINER_FLAG) !== 0;
  const byteLength = rawSize & SIZE_MASK;
  const payloadOffset = reader.tell();

  if (isContainer) {
    const children = parseRange(reader, payloadOffset, payloadOffset + byteLength);
    reader.seek(payloadOffset + byteLength);
    return { kind: "container", tag, headerOffset, payloadOffset, byteLength, children };
  }

  const data = reader.bytes(byteLength);
  return { kind: "leaf", tag, headerOffset, payloadOffset, byteLength, data };
}

/** Walks a contiguous run `[start, end)` of sibling chunks. */
function parseRange(reader: BinaryReader, start: number, end: number): ChunkNode[] {
  const saved = reader.tell();
  reader.seek(start);
  const nodes: ChunkNode[] = [];
  // Every Red Baron chunk stream we've walked is perfectly contiguous
  // (docs/formats/chunk-container.md); a short trailing gap that can't fit
  // another header just ends the walk rather than erroring, but a
  // malformed tag inside that range is a hard error, not a resync point.
  while (reader.tell() + 8 <= end) {
    nodes.push(parseOne(reader));
  }
  if (reader.tell() !== end) {
    throw new Error(`chunk: range [${start}, ${end}) left ${end - reader.tell()} trailing byte(s) unconsumed`);
  }
  reader.seek(saved);
  return nodes;
}

/**
 * Walks a flat sequence of sibling chunks starting at `byteOffset` (default
 * 0) through to the end of `source` (or `byteLength` bytes past the start,
 * if given). This is the entry point for a whole chunk-framed file, e.g. an
 * `.SCR`/`.BMP`/`.PAL`/`.FNT` subfile's bytes, or a volume record's payload.
 */
export function parseChunks(
  source: ArrayBufferLike | ArrayBufferView,
  byteOffset = 0,
  byteLength?: number,
): ChunkNode[] {
  const reader = new BinaryReader(source, byteOffset, byteLength);
  const nodes: ChunkNode[] = [];
  while (reader.remaining() > 0) {
    nodes.push(parseOne(reader));
  }
  return nodes;
}

/** Finds the first direct child of `nodes` with the given tag (e.g. `"BIN:"`). */
export function findChunk(nodes: readonly ChunkNode[], tag: string): ChunkNode | undefined {
  return nodes.find((node) => node.tag === tag);
}

/** Like `findChunk`, but throws if the tag isn't present among the direct children. */
export function requireChunk(nodes: readonly ChunkNode[], tag: string, context: string): ChunkNode {
  const node = findChunk(nodes, tag);
  if (!node) {
    throw new Error(`chunk: expected a '${tag}' chunk in ${context}, found [${nodes.map((n) => n.tag).join(", ")}]`);
  }
  return node;
}

/** Like `requireChunk`, but also asserts the node is a leaf and returns its raw payload bytes. */
export function requireLeaf(nodes: readonly ChunkNode[], tag: string, context: string): Uint8Array {
  const node = requireChunk(nodes, tag, context);
  if (node.kind !== "leaf") {
    throw new Error(`chunk: expected '${tag}' in ${context} to be a leaf chunk, found a container`);
  }
  return node.data;
}

/**
 * Recursively walks a whole chunk tree (containers and their descendants)
 * and returns every node matching `predicate`, in document order. Useful
 * for a caller that knows, from the (extension, tag) table in
 * docs/formats/chunk-container.md, which tags to look for wherever they
 * appear in a tree — e.g. every `TT3:` chunk in a `.TTM` file's flat
 * `VER:`/`PAG:`/`TT3:`/`TTI:` stream, without hard-coding that specific
 * shape here.
 */
export function collectChunks(nodes: readonly ChunkNode[], predicate: (node: ChunkNode) => boolean): ChunkNode[] {
  const matches: ChunkNode[] = [];
  const visit = (list: readonly ChunkNode[]): void => {
    for (const node of list) {
      if (predicate(node)) matches.push(node);
      if (node.kind === "container") visit(node.children);
    }
  };
  visit(nodes);
  return matches;
}
